import React from 'react';
import * as Icons from 'react-icons/fa6';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
} from '@chakra-ui/react';
import ComponentFactory from './Factory/ComponentFactory';

const ModalComponent = ({
  size,
  isOpen,
  onClose,
  modalContent,
  handleButtonAction,
}) => {
  const hasModalIcons = modalContent?.icons?.length > 0;

  /**
   * Renders icons dynamically based on the provided configuration.
   * @param {Array} icons - Array of icon configuration objects.
   */
  const renderIcons = (icons) => {
    if (!icons || icons.length === 0) return null;

    return icons.map((icon, index) => {
      const IconComponent = icon.name ? Icons[icon.name] : null;

      if (!IconComponent) {
        console.warn(`Icon "${icon.name}" not found.`);
        return null;
      }

      const variantStyles = icon.variant
        ? {
            size: `var(--icon-${icon.variant}-size, 36px)`,
            color: `var(--icon-${icon.variant}-color, var(--vibrant-purple))`,
          }
        : {};

      return (
        <IconComponent
          key={index}
          style={{
            position: icon.position ? 'absolute' : 'relative',
            top: icon.position?.top || '0px',
            left: icon.position?.left || '0px',
            width: icon.size || variantStyles.size,
            height: icon.size || variantStyles.size,
            color: icon.color || variantStyles.color,
          }}
          aria-hidden="true"
        />
      );
    });
  };

  return (
    <Modal
      onClose={onClose}
      size={size || 'sm'}
      isOpen={isOpen}
      motionPreset="scale"
      blockScrollOnMount={false}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        style={{
          backgroundColor: 'var(--color-yellow-50, #FDF9F0)',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
        }}
        role="dialog"
        aria-modal="true"
      >
        {/* Left vertical icon container */}
        {hasModalIcons && (
          <div
            style={{
              width: 'var(--modal-icon-column-width, 70px)',
              minWidth: 'var(--modal-icon-column-width, 70px)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 0 20px 12px',
            }}
            aria-hidden="true"
          >
            {renderIcons(modalContent.icons)}
          </div>
        )}

        {/* Right content container */}
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          {/* Header */}
          {modalContent?.title && (
            <ModalHeader
              id="modal-title"
              pl={hasModalIcons ? 0 : 6}
              pb={1}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: `var(--${modalContent.variant}-font-family, Sora)`,
                fontWeight: `var(--${modalContent.variant}-font-weight, 700)`,
                fontSize: `var(--${modalContent.variant}-font-size, 18px)`,
                lineHeight: `var(--${modalContent.variant}-line-height, 25.2px)`,
                color: `var(--${modalContent.variant}-color, var(--sutter-sync-purple))`,
                textAlign: `var(--${modalContent.variant}-text-align, left)`,
              }}
            >
              <span style={{ flex: 1 }}>{modalContent.title}</span>
            </ModalHeader>
          )}

          {/* Close Button */}
          <ModalCloseButton
            aria-label="Close modal"
            size={modalContent?.closeButton?.size || 'md'}
            style={{
              color: modalContent?.closeButton?.color || 'var(--color-dusky-teal, #203433)',
              fontSize: modalContent?.closeButton?.size === 'sm' ? '14px' : modalContent?.closeButton?.size === 'lg' ? '24px' : '18px',
            }} />

          {/* Body */}
          <ModalBody
            id="modal-description"
            pl={hasModalIcons ? 0 : 6}
            pb={1}
          >
            {modalContent?.customContent ? (
              modalContent.customContent
            ) : (
              modalContent?.content?.map((contentItem, index) => (
                <ComponentFactory
                  key={index}
                  section={contentItem}
                  handleButtonAction={handleButtonAction} />
              ))
            )}
          </ModalBody>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ModalComponent;